import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { logging } from '../../../actions/log';
import styles from './CommentReview.module.less';
import { byteLength } from '../../../utils/utils';
import { postForumAddReview, setShowForumReview, setLenovoSoft } from '../../../actions/forum';
import CheckSoft from '../../../components/Forum/CheckSoft/CheckSoft';

import Toast from '../../../components/Toast/Toast';
import xss from 'xss';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);
const selectForumAddReview = createSelector(
  state => state.forum,
  forum => forum.forumAddReview,
);
const selectUserInfo = createSelector(
  state => state.user,
  user => user.userInfo,
);

export default function ForumReview({ logInfo = {}, comment, publishCallback }) {
  const dispatch = useDispatch();

  const logInfoData = useSelector(selectLogInfo);
  const userInfo = useSelector(selectUserInfo);
  const forumAddReview = useSelector(selectForumAddReview);
  const [checkSoftList, setCheckSoftList] = useState([]);
  const [value, setValue] = useState('');
  // const [isNoValue, setIsNoValue] = useState(null);
  const [submitTime, setSubmitTime] = useState(+new Date());
  const [isSubmitLogin, setIsSubmitLogin] = useState(false);
  const [updateCheckSoft, setUpdateCheckSoft] = useState(false);

  const maxLength = 300;

  useEffect(() => {
    return () => {
      dispatch(setLenovoSoft({ word: '', pageKey: 'review', data: {} }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (forumAddReview.submitTime === submitTime && !forumAddReview.loading) {
      dispatch(
        logging({
          category: 'R',
          action: 'Detail_Review_Result',
          label: '',
          param2: {
            source: logInfoData.source,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            id: comment.bizId,
            commentId: forumAddReview.originalCommentId,
            reviewId: forumAddReview.bizId,
            status: forumAddReview.status,
            type: comment.type,
          },
        }),
      );

      if (forumAddReview.submitTime === submitTime && forumAddReview.status === 0) {
        setValue('');
        setCheckSoftList([]);
        setUpdateCheckSoft(true);
        // eslint-disable-next-line no-unused-expressions
        publishCallback && publishCallback();
      }
    }
  }, [forumAddReview, comment, submitTime]);

  const handleAddComment = useCallback(() => {
    // 设置显示
    if (!value) {
      Toast.error({ content: '请输入评论内容！' });
      return;
    }

    // 验证字数
    if (byteLength(value) > maxLength) {
      Toast.error({ content: '输入内容超过最大字数！' });
      return;
    }

    // 获取用户是否登录
    // eslint-disable-next-line camelcase
    const login_status = window.CallNativeMethod('get_login_status');
    // eslint-disable-next-line camelcase
    if (login_status !== '1') {
      // 未登录，弹出登录框
      window.CallNativeMethod('login');
      setIsSubmitLogin(true);
      return;
    }

    const time = +new Date();
    setSubmitTime(time);

    dispatch(
      logging({
        category: 'C',
        action: 'Detail_Review_Submit',
        label: '',
        param2: {
          source: logInfo.source,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          passInfos: checkSoftList,
          id: comment.bizId,
          commentId: comment.originalCommentId,
          reviewId: comment.id,
          type: comment.type,
        },
      }),
    );
    dispatch(
      postForumAddReview({
        bizIdentity: comment.bizIdentity,
        content: encodeURIComponent(xss(`@${comment.userNickname || '商店用户'}:${value}`)),
        originalCommentId: comment.originalCommentId,
        parentId: comment.commentId,
        grade: 5,
        passInfos: checkSoftList,
        type: comment.type,
        bizId: comment.bizId,
        commentUserId: comment.userId,
        submitTime: time,
        source: 'message',
      }),
    );
  }, [comment, value, checkSoftList]);

  useEffect(() => {
    if (isSubmitLogin === true) {
      // eslint-disable-next-line camelcase
      const login_status = window.CallNativeMethod('get_login_status');
      // eslint-disable-next-line camelcase
      if (login_status === '1' && userInfo.st) {
        handleAddComment();
        setIsSubmitLogin(false);
      }
    }
  }, [userInfo, isSubmitLogin]);

  const handleChange = useCallback(ev => {
    ev.persist();
    const inputValue = ev.target.value;
    setValue(inputValue);
  });

  const handleFocus = useCallback(ev => {
    ev.persist();
  });

  const handleCancel = useCallback(() => {
    dispatch(
      logging({
        category: 'C',
        action: 'Detail_Review_Close',
        label: '',
        param2: {
          source: logInfoData.source,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          passInfos: checkSoftList,
          id: comment.bizId,
          commentId: comment.originalCommentId,
          reviewId: comment.id,
          type: comment.type,
        },
      }),
    );
    dispatch(setShowForumReview(null));
    dispatch(setLenovoSoft({ word: '', pageKey: 'review', data: {} }));
  }, [logInfoData, checkSoftList, comment]);

  useEffect(() => {
    return () => {
      handleCancel();
    };
  }, []);

  return (
    <div className={styles.review}>
      <div className={styles.comment_textarea_box}>
        <textarea
          className={styles.comment_textarea}
          placeholder={`回复 ${(comment && comment.userNickname) || '商店用户'}`}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
        />
        <div className={styles.comment_textarea_num}>
          <span className={byteLength(value) > maxLength ? styles.textarea_num_max : ''}>
            {byteLength(value)}
          </span>
          <span> / {maxLength}</span>
        </div>
      </div>
      <div className={styles.comment_bottom}>
        <div>
          <div className={styles.checkSoftList}>
            {comment.type === '3' && (
              <CheckSoft
                setCheckSoftList={setCheckSoftList}
                checkSoftList={checkSoftList}
                updateCheckSoft={updateCheckSoft}
                pageKey={'review'}
              />
            )}
          </div>
        </div>
        {value === '' ? (
          <div className={styles.comment_btn_disabled}>发表</div>
        ) : (
          <div className={styles.comment_btn} onClick={handleAddComment}>
            发表
          </div>
        )}
      </div>
    </div>
  );
}
